import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Modal, ModalFooter } from "./common/Modal";
import { Button, Pill } from "./common/Primitives";
import api, { formatApiError, API_BASE } from "../lib/api";
import { formatDate } from "../lib/datetime";
import { Share2, Copy, RefreshCcw, Ban, ExternalLink, Eye, Clock, AlertTriangle } from "lucide-react";

const EXPIRY_PRESETS = [
  { label: "24 hours", days: 1 },
  { label: "7 days",   days: 7 },
  { label: "30 days",  days: 30 },
  { label: "Never",    days: null },
];

function formatExpiryHint(iso) {
  if (!iso) return "Never expires";
  try {
    const d = new Date(iso);
    const now = new Date();
    const hrs = Math.max(0, Math.round((d - now) / 36e5));
    if (hrs <= 0) return "Expired";
    if (hrs < 48) return `Expires in ${hrs}h`;
    const days = Math.round(hrs / 24);
    return `Expires in ${days}d · ${formatDate(iso)}`;
  } catch { return "Expires " + iso.slice(0, 10); }
}

/**
 * Founder-facing modal for creating, viewing, rotating, revoking and expiring
 * a public-read passport share link.
 */
export default function PassportShareModal({ open, onClose }) {
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiryDays, setExpiryDays] = useState(7); // default 7 days

  // Share URL points to the backend OG-friendly HTML endpoint so pastes into
  // WhatsApp / LinkedIn / email produce a rich preview. Humans get an
  // instant meta-refresh + JS redirect to the React SPA page.
  const publicUrl = share
    ? `${API_BASE}/public/passport/${share.share_token}/share`
    : null;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/startup/passport/share");
      setShare(r.data?.share || null);
    } catch (e) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post("/startup/passport/share", { expires_in_days: expiryDays });
      setShare(r.data);
      toast.success("Share link ready — copy it to your intro email.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke the current share link? Anyone who already has it will lose access.")) return;
    try {
      await api.delete("/startup/passport/share");
      setShare(null);
      toast.success("Share link revoked");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Could not copy — please select and copy manually");
    }
  };

  return (
    <>
      <Toaster richColors position="top-right" />
      <Modal
        open={open}
        onClose={onClose}
        title="Share Your Verified Passport"
        subtitle="A public, read-only URL so warm intros land with your score already visible."
        size="md"
        testId="passport-share-modal"
      >
        <div className="text-sm text-ink-soft space-y-4">
          <p>
            The recipient sees your <strong>Trust Score</strong>, verification pillars, and 30-day trend — but never any raw financials, uploaded documents, or investor-locked fields. Pasting the link into WhatsApp, email or LinkedIn produces a rich preview card with your score.
          </p>

          {/* Expiry picker (only when generating a new link) */}
          {!share && (
            <div data-testid="passport-share-expiry-picker">
              <div className="overline mb-2 flex items-center gap-1"><Clock size={12} /> LINK EXPIRY</div>
              <div className="flex flex-wrap gap-2">
                {EXPIRY_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setExpiryDays(p.days)}
                    className={`px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider transition border ${
                      expiryDays === p.days
                        ? "bg-navy text-cream border-navy"
                        : "bg-white text-ink-soft border-navy/15 hover:border-gold"
                    }`}
                    data-testid={`passport-share-expiry-${p.days ?? "never"}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-xs font-mono text-ink-muted" data-testid="passport-share-loading">Loading…</div>
          ) : share ? (
            <div className={`border rounded p-4 space-y-2 ${share.is_expired ? "border-rose-400/50 bg-rose-50/30" : "border-gold/40 bg-gold/[0.06]"}`}
                  data-testid="passport-share-active">
              <div className="flex items-center justify-between">
                <div className="overline">{share.is_expired ? "EXPIRED LINK" : "ACTIVE LINK"}</div>
                <div className="text-[11px] font-mono text-ink-muted flex items-center gap-1" data-testid="passport-share-expiry-line">
                  <Clock size={11} /> {formatExpiryHint(share.expires_at)}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="flex-1 min-w-0 text-xs bg-white border border-navy/10 rounded px-2 py-2 font-mono truncate" data-testid="passport-share-url">
                  {publicUrl}
                </code>
                <Button size="sm" onClick={copy} disabled={share.is_expired} data-testid="passport-share-copy">
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-gold hover:text-gold/80 px-2 py-2"
                  data-testid="passport-share-open"
                >
                  <ExternalLink size={12} /> Preview
                </a>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-mono text-ink-muted flex-wrap">
                <Pill tone="gold" testId="passport-share-views">
                  <Eye size={10} className="mr-1" /> {share.view_count || 0} views
                </Pill>
                <span>Minted {(share.created_at || "").slice(0, 10)}</span>
              </div>
              {share.is_expired && (
                <div className="flex items-center gap-1 text-[11px] text-rose-700 font-mono" data-testid="passport-share-expired-msg">
                  <AlertTriangle size={11} /> This link has expired. Generate a fresh one to keep sharing.
                </div>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-navy/20 bg-white/60 rounded p-6 text-center" data-testid="passport-share-empty">
              <Share2 size={22} className="mx-auto mb-2 text-ink-faint" />
              <div className="text-sm text-ink-muted">
                No share link yet. Pick an expiry above and generate one below.
              </div>
            </div>
          )}
        </div>

        <ModalFooter>
          {share ? (
            <>
              <Button variant="outline" size="sm" onClick={revoke} data-testid="passport-share-revoke">
                <Ban size={12} className="mr-1" /> Revoke
              </Button>
              <Button size="sm" onClick={create} disabled={creating} data-testid="passport-share-rotate">
                <RefreshCcw size={12} className="mr-1" />
                {creating ? "Rotating…" : "Rotate Link"}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={create} disabled={creating} data-testid="passport-share-create">
              <Share2 size={12} className="mr-1" />
              {creating ? "Generating…" : "Generate Share Link"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
}
